// Updated DeleteConfirmationModal.tsx with better folder deletion warnings

import { AlertTriangle, Trash2, X, Folder, File } from 'lucide-react'

interface DeleteConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  items: Array<{ name: string; type: 'file' | 'folder' }>
  isLoading: boolean
}

export function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  items,
  isLoading
}: DeleteConfirmationModalProps) {
  if (!isOpen) return null

  const fileCount = items.filter(item => item.type === 'file').length
  const folderCount = items.filter(item => item.type === 'folder').length
  const hasFolders = folderCount > 0

  const getConfirmationText = () => {
    if (items.length === 1) {
      const item = items[0]
      if (item.type === 'folder') {
        return `Are you sure you want to delete the folder "${item.name}" and ALL its contents?`
      }
      return `Are you sure you want to delete "${item.name}"?`
    }
    
    const parts = []
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount > 1 ? 's' : ''}`)
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount > 1 ? 's' : ''}`)
    
    const itemText = parts.join(' and ')
    return hasFolders 
      ? `Are you sure you want to delete ${itemText}? All folder contents will also be deleted.`
      : `Are you sure you want to delete ${itemText}?`
  }

  const getWarningText = () => {
    if (hasFolders) {
      return folderCount === 1
        ? 'This folder and all files/subfolders inside it will be moved to trash.'
        : 'These folders and all files/subfolders inside them will be moved to trash.'
    }
    return 'Items will be moved to trash (recoverable for 30 days).'
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-600 rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${hasFolders ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
              <AlertTriangle className={`w-6 h-6 ${hasFolders ? 'text-red-400' : 'text-orange-400'}`} />
            </div>
            <h2 className="text-xl font-semibold text-white">
              {hasFolders ? 'Delete Folders & Contents' : 'Confirm Delete'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-dark-300 mb-4">
            {getConfirmationText()}
          </p>
          
          {/* Items list */}
          {items.length > 1 && (
            <div className="bg-dark-900/50 rounded-lg p-4 mb-4 max-h-32 overflow-y-auto">
              <p className="text-sm font-medium text-dark-400 mb-2">Items to delete:</p>
              <ul className="space-y-1">
                {items.slice(0, 10).map((item, index) => (
                  <li key={index} className="text-sm text-dark-300 flex items-center gap-2">
                    {item.type === 'file' ? (
                      <File className="w-3 h-3 text-blue-400" />
                    ) : (
                      <Folder className="w-3 h-3 text-yellow-400" />
                    )}
                    <span>{item.name}</span>
                    {item.type === 'folder' && (
                      <span className="text-xs text-red-400 ml-1">(+ contents)</span>
                    )}
                  </li>
                ))}
                {items.length > 10 && (
                  <li className="text-sm text-dark-400 italic">
                    ... and {items.length - 10} more items
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Warning */}
          <div className={`border rounded-lg p-3 mb-6 ${
            hasFolders 
              ? 'bg-red-500/10 border-red-500/20' 
              : 'bg-orange-500/10 border-orange-500/20'
          }`}>
            <p className={`text-sm flex items-center gap-2 ${
              hasFolders ? 'text-red-300' : 'text-orange-300'
            }`}>
              <AlertTriangle className="w-4 h-4" />
              {getWarningText()}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 btn btn-ghost disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 btn text-white disabled:opacity-50 flex items-center justify-center gap-2 ${
              hasFolders ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                {hasFolders ? 'Delete All' : 'Delete'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}